import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Button, Checkbox } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import React, { useCallback, useState } from "react";
import { CSVLink } from "react-csv";
import { debounce } from "../../../../../utils";
import "../../../../Admin/Reports/Reports Detailed/ReportsDetailed.css";
import { BreadcrumbAdmin } from "../../../../UI/BreadcrumbAdmin";
import { Search } from "../../../../UI/Search/Search";
import { useOutsideClick } from "../../../../../hooks/useOutsideClick";
import { useLazyGetSuperAdminEmployeeSequentialProgressQuery } from "../../../../../redux-toolkit/features/reports/ReportsApiSlice";
import { EmployeeSequentialProgressTable } from "./EmployeeSequentialProgressTable";

const list = [
  { name: "employee", label: "Employee" },
  { name: "company", label: "Company" },
  { name: "currentBookName", label: "Current Book Name" },
  { name: "booksCompleted", label: "Books Completed" },
  { name: "booksRemaining", label: "Books Remaining" },
  { name: "currentBookOrder", label: "Current Book #" },
  { name: "tasksDoneAll", label: "Tasks Done (All Books)" },
  { name: "totalTasksAll", label: "Total Tasks (All Books)" },
  { name: "overallProgressPercent", label: "Overall Progress %" },
  { name: "tasksDoneCurrent", label: "Tasks Done (Current Book)" },
  { name: "totalTasksCurrent", label: "Total Tasks (Current Book)" },
  { name: "currentProgressPercent", label: "Current Progress %" },
  { name: "status", label: "Status" },
  { name: "waitingSince", label: "Waiting Since" },
];

const formatPercent = (value) =>
  value == null ? "0%" : `${Number(value).toFixed(1)}%`;

const formatWaitingSince = (value) => {
  if (!value) return "--";
  try {
    return new Date(value).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "--";
  }
};

const mapRowsToCsv = (rows = []) =>
  rows.map((obj) => ({
    Employee: obj.employeeName ?? "",
    Company: obj.company?.name ?? "",
    "Current Book Name": obj.currentBookName ?? "",
    "Books Completed": obj.booksCompleted ?? 0,
    "Books Remaining": obj.booksRemaining ?? 0,
    "Current Book #": obj.currentBookOrder ?? "",
    "Tasks Done (All Books)": obj.tasksDoneAll ?? 0,
    "Total Tasks (All Books)": obj.totalTasksAll ?? 0,
    "Overall Progress %": formatPercent(obj.overallProgressPercent),
    "Tasks Done (Current Book)": obj.tasksDoneCurrent ?? 0,
    "Total Tasks (Current Book)": obj.totalTasksCurrent ?? 0,
    "Current Progress %": formatPercent(obj.currentProgressPercent),
    Status: obj.status ?? "",
    "Waiting Since": formatWaitingSince(obj.waitingSince),
  }));

export const EmployeeSequentialProgress = () => {
  const [select, setSelect] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [csvData, setCsvData] = useState([]);
  const ref = React.useRef(null);
  const [fetchAllRows] = useLazyGetSuperAdminEmployeeSequentialProgressQuery();

  const closePopup = () => {
    setSelect(false);
  };

  useOutsideClick(ref, closePopup);

  const [check, setCheck] = useState({
    employee: true,
    company: true,
    currentBookName: true,
    booksCompleted: true,
    booksRemaining: true,
    currentBookOrder: true,
    tasksDoneAll: true,
    totalTasksAll: true,
    overallProgressPercent: true,
    tasksDoneCurrent: true,
    totalTasksCurrent: true,
    currentProgressPercent: true,
    status: true,
    waitingSince: true,
  });

  const base = [
    {
      _id: 1,
      link: "/super-admin/reports",
      label: "Reports",
    },
  ];

  const setDebouncedSearchQuery = useCallback(
    debounce((value) => {
      setSearchQuery(value);
    }, 500),
    []
  );

  const handleChange = (e) => {
    setCheck({ ...check, [e.target.name]: e.target.checked });
  };

  return (
    <div className="container">
      <BreadcrumbAdmin base={base} page="Employee Sequential Progress" />
      <p className="reportsDetailed__subtitle">
        Each employee has one current book. All prior books are completed. All
        later books are locked until the current one is approved.
      </p>
      <div className="reportsDetailed__head">
        <Search
          mini
          setSearchVal={(val) => setDebouncedSearchQuery(val)}
          placehold="Search by name, company, book..."
        />
        <div className="reportsDetailed__headright">
          <div ref={ref} className="reports__selectLayout">
            <Button
              variant="outlined"
              endIcon={
                <ArrowForwardIosIcon
                  sx={{
                    transform: select ? "rotate(-90deg)" : "rotate(90deg)",
                    color: "grey",
                    fontSize: "16px !important",
                  }}
                />
              }
              sx={{
                color: "var(--grey400)",
                borderColor: "var(--grey300)",
                width: "150px",
                fontWeight: "400 !important",
              }}
              onClick={() => setSelect(!select)}
            >
              Manage Field
            </Button>
            <div
              className={select ? "reports__selectActive" : "reports__select"}
            >
              <div className="reports__selectcontent">
                {list.map((item, index) => (
                  <FormControlLabel
                    key={index}
                    control={
                      <Checkbox
                        checked={Boolean(check[item.name])}
                        name={item.name}
                        onChange={handleChange}
                        sx={{ "& .MuiSvgIcon-root": { fontSize: "20px" } }}
                      />
                    }
                    label={
                      <span style={{ fontSize: "15px" }}>{item.label}</span>
                    }
                  />
                ))}
              </div>
            </div>
          </div>
          <CSVLink
            filename="Employee-sequential-progress"
            data={csvData}
            asyncOnClick
            onClick={async (_event, done) => {
              try {
                const result = await fetchAllRows(
                  {
                    sortBy: "",
                    type: "",
                    query: searchQuery,
                    exportAll: true,
                  },
                  { preferCacheValue: false }
                ).unwrap();
                setCsvData(mapRowsToCsv(result?.data ?? []));
                done(true);
              } catch {
                done(false);
              }
            }}
          >
            <Button
              variant="contained"
              endIcon={<FileUploadOutlinedIcon sx={{ color: "white" }} />}
            >
              Export
            </Button>
          </CSVLink>
        </div>
      </div>

      <div className="reports__table">
        <EmployeeSequentialProgressTable
          check={check}
          searchQuery={searchQuery}
          getTableData={(payload) => {
            setCsvData(mapRowsToCsv(payload?.data ?? []));
          }}
        />
      </div>
    </div>
  );
};
